import { FC, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import ReactCrop from 'react-image-crop';
import { getCropMinWidth } from 'utils/campaign/cropModal';
import { BANNER_IMAGE_CROP_TYPES_CONFIG, BannerImageCropType } from 'utils/constants/banner';

import { Card, Loader } from '@hh.ru/magritte-ui';

import { CropInfo, setBannerImageCropInfoByType, setBannerImageIsLoadedInCropByType } from 'store/models/createBanner';
import {
    setQuickCampaignBannerImageCropInfoByType,
    setQuickCampaignBannerImageIsLoadedInCropByType,
} from 'store/models/createQuickCampaign';
import { useDispatch } from 'store/utils';

import styles from 'components/common/BannerImg/CropImageModal/ImageContent/ImageCrop/ImageCrop.module.less';

interface ImageCropProps {
    cropType: BannerImageCropType;
    cropInfo: CropInfo;
    width: number;
    height: number;
    url: string;
    isLoadedInCrop?: boolean;
    isQuickCampaign?: boolean;
}

export const ImageCrop: FC<ImageCropProps> = ({
    cropType,
    cropInfo,
    height,
    width,
    url,
    isLoadedInCrop,
    isQuickCampaign,
}) => {
    const [cropMinWidth, setCropMinWidth] = useState(0);
    const [imageMaxSize, setImageMaxSize] = useState<{ width: number; height: number } | null>(null);
    const { minWidth, aspect } = BANNER_IMAGE_CROP_TYPES_CONFIG[cropType];

    const dispatch = useDispatch();

    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (ref.current && width && height) {
            const cropContainerSize = ref.current.offsetWidth;
            setCropMinWidth(getCropMinWidth(cropContainerSize, width, height, minWidth));

            const imageAspectRatio = width / height;
            const containerAspectRatio = 1;

            let maxWidth: number;
            let maxHeight: number;

            if (imageAspectRatio > containerAspectRatio) {
                maxWidth = cropContainerSize;
                maxHeight = cropContainerSize / imageAspectRatio;
            } else {
                maxWidth = cropContainerSize * imageAspectRatio;
                maxHeight = cropContainerSize;
            }

            setImageMaxSize({ width: maxWidth, height: maxHeight });
        }
    }, [width, height, minWidth]);

    return (
        <Card ref={ref} style="tertiary" stretched padding={0}>
            <div className={styles.imageCropContent}>
                {!isLoadedInCrop && (
                    <div className={styles.imageCropContentImgLoading}>
                        <Loader size={32} />
                    </div>
                )}
                <ReactCrop
                    crop={cropInfo.percentCrop}
                    aspect={aspect}
                    className={styles.imageCropContentWrapper}
                    onChange={(crop, percentCrop) => {
                        const cropInfo = {
                            crop,
                            percentCrop,
                        };

                        dispatch(
                            isQuickCampaign
                                ? setQuickCampaignBannerImageCropInfoByType({ cropType, cropInfo })
                                : setBannerImageCropInfoByType({ cropType, cropInfo })
                        );
                    }}
                    minWidth={cropMinWidth}
                    keepSelection
                >
                    <img
                        src={url}
                        className={classNames(
                            styles.imageCropContentImg,
                            !isLoadedInCrop ? styles.imageCropContentImgHidden : ''
                        )}
                        style={
                            imageMaxSize
                                ? {
                                      maxWidth: `${imageMaxSize.width}px`,
                                      maxHeight: `${imageMaxSize.height}px`,
                                  }
                                : undefined
                        }
                        draggable="false"
                        onLoad={() => {
                            dispatch(
                                isQuickCampaign
                                    ? setQuickCampaignBannerImageIsLoadedInCropByType({
                                          cropType,
                                          isLoadedInCrop: true,
                                      })
                                    : setBannerImageIsLoadedInCropByType({ cropType, isLoadedInCrop: true })
                            );
                        }}
                    />
                </ReactCrop>
            </div>
        </Card>
    );
};
